import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { CaseTypeRepository } from '../../../infrastructure/repositories/case-type.repository';
import { CaseTypeState } from '../../states/case-type.state';
import { GetCaseTypeByIdQuery } from './get-case-type-by-id.query';
import { GetCaseTypeByIdResult } from './get-case-type-by-id.result';

/**
 * Use case for retrieving a single case type by ID.
 * Manages loading state, fetches data from repository, and updates state.
 */
@Injectable({ providedIn: 'root' })
export class GetCaseTypeByIdUseCase {
  private readonly repository = inject(CaseTypeRepository);
  private readonly state = inject(CaseTypeState);

  /**
   * Executes the use case to get a case type by ID.
   * @param query - Query containing the case type ID
   * @returns Observable with the result
   */
  public execute(query: GetCaseTypeByIdQuery): Observable<GetCaseTypeByIdResult> {
    this.state.setLoading(true);
    this.state.clearError();

    return this.repository.getCaseTypeById(query.id).pipe(
      tap((caseType) => {
        this.state.setCurrentCaseType(caseType);
        this.state.setLoading(false);
      }),
      map((caseType) => ({ caseType })),
      catchError((error) => {
        const errorMessage = error?.error?.message || error?.message || 'Failed to load case type';
        this.state.setError(errorMessage);
        this.state.setLoading(false);
        throw error;
      })
    );
  }
}
